import type { ShopCatalogItem, ShopCatalogVariant } from './shopApi';

/**
 * Pure helpers for reading shop catalogue data — prices, stock and variant choice — shared by the listing,
 * the product page and the basket so they all agree.
 */

/** Query-string key that remembers the chosen variant on a product page: `/sklep/farba?wariant=IGORA-6-0`. */
export const VARIANT_QUERY_PARAM = 'wariant';

/** SKUs are compared trimmed and ignoring case — exactly as the API compares them. */
export const skuKey = (sku: string | null | undefined): string => (sku ?? '').trim().toLowerCase();

/** True when both SKUs are present and name the same thing. */
export const sameSku = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const left = skuKey(a);
  return left !== '' && left === skuKey(b);
};

/** "12,50 zł" — the single price format used across the shop. */
export const formatPrice = (price: number): string => price.toFixed(2).replace('.', ',') + ' zł';

/** What the customer pays: the discount price when there is one (the server charges `discountPrice ?? price`). */
export const effectivePrice = (price: number, discountPrice?: number | null): number => discountPrice ?? price;

/** Whether to show the regular price struck through next to a lower discount price. */
export const hasDiscount = (price: number, discountPrice?: number | null): discountPrice is number =>
  discountPrice != null && discountPrice < price;

/** Whole-percent discount for a "-20%" badge, or null when there is no visible discount. */
export const discountPercent = (price?: number | null, discountPrice?: number | null): number | null => {
  if (price == null || price <= 0 || !hasDiscount(price, discountPrice)) return null;
  const percent = Math.round((1 - discountPrice / price) * 100);
  return percent > 0 ? percent : null;
};

export const isInStock = (stockQuantity: number | null | undefined): boolean => (stockQuantity ?? 0) > 0;

/** The variant with this SKU (case-insensitive), whatever its stock. */
export const findVariantBySku = (
  variants: ShopCatalogVariant[],
  sku: string | null | undefined
): ShopCatalogVariant | undefined => variants.find((variant) => sameSku(variant.sku, sku));

/**
 * The variant a product page shows: the one named in `?wariant=` (even when sold out, so a shared link shows
 * that it is sold out), otherwise the first one in stock, otherwise none.
 */
export const resolveSelectedVariant = (
  variants: ShopCatalogVariant[],
  requestedSku: string | null | undefined
): ShopCatalogVariant | null =>
  findVariantBySku(variants, requestedSku) ?? variants.find((variant) => isInStock(variant.stockQuantity)) ?? null;

/**
 * Fills in what an API from before variants and bundles does not send, so callers can rely on `kind`.
 * The tenant frontend and the API are deployed separately, so either may be ahead of the other.
 */
export const normalizeCatalogItem = (raw: ShopCatalogItem): ShopCatalogItem => ({
  ...raw,
  kind: raw.kind ?? 'product',
});
